<template>
    <section class="section">
        <div class="container-fluid">
            <div class="level mb-2">
                <div class="level-left">
                    <div class="level-item">
                        <div>
                            <a :href="urls.eventAdmin().bracketsListingPage(eventId)" class="button is-light is-small mb-3">
                                <span class="icon"><i class="fas fa-arrow-left"></i></span>
                                <span>{{ translate("discipline_packages.back_to_brackets") }}</span>
                            </a>
                            <h1 class="title mb-1">{{ translate("discipline_packages.title") }}</h1>
                            <p class="subtitle is-6">{{ translate("discipline_packages.subtitle") }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="store.loading" class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <template v-else>
                <Notification v-if="store.unconfiguredCount > 0" class="is-warning mb-4" :centered="false">
                    <span class="icon"><i class="fas fa-exclamation-triangle" /></span>
                    {{ translate("discipline_packages.disciplines_not_configured_warning", false, [store.unconfiguredCount]) }}
                </Notification>

                <Notification
                    v-if="store.uncoveredCategories > 0 && store.nonOrphanedPackages.length > 0"
                    class="is-warning mb-4"
                    :centered="false"
                >
                    <span class="icon"><i class="fas fa-exclamation-triangle" /></span>
                    {{ translate("discipline_packages.uncovered_categories_warning", false, [store.uncoveredCategories]) }}
                    <a :href="urls.eventAdmin().categoriesManagementPage(eventId)" class="ml-2">
                        {{ translate("discipline_packages.uncovered_categories_fix_link") }}
                        <span class="icon is-small"><i class="fas fa-arrow-right" /></span>
                    </a>
                </Notification>

                <Notification v-if="store.orphanedPackages.length > 0" class="is-danger mb-4" :centered="false">
                    <p class="mb-2">
                        <span class="icon"><i class="fas fa-exclamation-circle"></i></span>
                        {{ translate("discipline_packages.orphaned_warning", false, [store.orphanedPackages.length]) }}
                    </p>
                    <div class="tags">
                        <span v-for="pkg in store.orphanedPackages" :key="pkg.discipline" class="tag is-danger is-medium">
                            {{ translate(`discipline_packages.discipline.${pkg.discipline}`) }}
                            <button class="delete is-small" @click="store.deletePackage(pkg.discipline)"></button>
                        </span>
                    </div>
                </Notification>

                <Notification v-if="store.nonOrphanedPackages.length === 0" class="is-warning mb-4" :centered="false">
                    <span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
                    {{ translate("discipline_packages.no_disciplines_detected") }}
                    <a :href="urls.eventAdmin().categoriesManagementPage(eventId)" class="ml-2">
                        {{ translate("discipline_packages.uncovered_categories_fix_link") }}
                        <span class="icon is-small"><i class="fas fa-arrow-right" /></span>
                    </a>
                </Notification>

                <template v-else>
                    <div class="tabs is-toggle is-rounded mb-5">
                        <ul>
                            <li
                                v-for="pkg in store.nonOrphanedPackages"
                                :key="pkg.discipline"
                                :class="{ 'is-active': store.selectedDiscipline === pkg.discipline }"
                            >
                                <a @click="store.selectDiscipline(pkg)">
                                    <span>{{ translate(`discipline_packages.discipline.${pkg.discipline}`) }}</span>
                                    <span class="tag is-rounded is-small ml-2" :class="pkg.isConfigured ? 'is-success' : 'is-warning'">
                                        <i :class="pkg.isConfigured ? 'fas fa-check' : 'fas fa-exclamation'"></i>
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DisciplineConfigPanel />
                </template>
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { onMounted } from "vue";
    import { translate } from "../Common/translations";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Notification from "../Common/Notification.vue";
    import { useDisciplinePackagesStore } from "./disciplinePackagesStore";
    import DisciplineConfigPanel from "./DisciplineConfigPanel.vue";
    import urls from "../Urls/urls";

    const props = defineProps<{
        eventId: number;
    }>();

    const store = useDisciplinePackagesStore();

    onMounted(() => {
        store.init(props.eventId);
    });
</script>
